import React, { useState, useContext } from "react";
import { mdiChevronRight } from "@mdi/js";
import Icon from "@mdi/react";
import { BetQLService, DEFAULT_BOOK_ID } from "@rotoql/common-services";

import { printPlus } from "../../services/DisplayService";
import { SubscriptionContext } from "../../services/SubscriptionService";
import AnalyticsService from "src/services/AnalyticsService";
import styles from "./style.scss";
import { MetaBetLink } from "src/services/OfferService";
import { BookIcon } from "src/components/BookImage";
import { DEFAULT_SPORT, STATE_MAP, WebSportItem } from "src/constants";
import { orderBooks } from "./GameLinesRow";
import { PERIODS } from "src/services/EventService";
interface MobileGameLinesRowProps {
  game: { [key: string]: any } | null;
  lineType: string;
  currentSportObj: WebSportItem | null;
  books: [];
  loading: boolean;
  size: any;
}

/*
 * The vertical list of sportsbooks and lines on a mobile display
 *
 * The component for mobile is different enough that we have a different component.
 */
const MobileGameLinesRow = (props: MobileGameLinesRowProps) => {
  const { lineType, game, currentSportObj, loading, size } = props;
  const context = useContext(SubscriptionContext);
  const [showAllBooks, setShowAllBooks] = useState<boolean>(false);

  /*
   * A single block displaying the information for a sportsbook's line.
   * This may be a Line Element but it looks like it is a sportsbook entry for the user so
   * it was named that way.  Functionally it takes a single line and produces as JSX
   * component to display to the user.
   *
   * The component is a line with three parts
   * - Sportsbook square logo and name
   * - A display string for the line information pertaining to our pick
   * - A chevron indicating they can click on the row to redirect them to signup to the sportsbook.
   *
   * @param ourPick {"away" | "home"} if our pick is the away team or home team
   * @param line the line to display the data for
   */
  const sportbookEl = (ourPick: "away" | "home" | "draw", book: any) => {
    const period = Object.values(PERIODS).find((_period) => lineType.includes(_period)) || "FG";
    const line = book.lines.find((l: any) => l.lineType === "current" && l.period === period) ?? {};

    // Compute the data for the line display string in the middle of the row
    const pickTeam = ourPick !== "draw" ? game?.[`${ourPick}Team`] ?? "N/A" : { lastName: "Draw" };
    let linetext: string = "--";
    if (lineType.indexOf("total") !== -1) {
      linetext = `O/U ${line.total ?? "--"}`;
    } else if (lineType.indexOf("spread") !== -1) {
      linetext = `${pickTeam.preferredAbbreviation} ${printPlus(line[`${ourPick}Spread`])}`;
    } else if (lineType.indexOf("money") !== -1) {
      linetext = `${pickTeam.preferredAbbreviation} ${printPlus(line[`${ourPick}Money`])}`;
    }

    const pageLocation = window?.location?.href;
    // don't show link if not in a legal state, if line is not available, or if the book is consensus
    const showBetNowLink =
      STATE_MAP[context.userLocale]?.legal && !linetext.includes("--") && book.id !== DEFAULT_BOOK_ID;

    return (
      <div className={`sportbook-con ${size}`} key={book.id}>
        <div className={`mobile-sportsbook-logo-name-con ${size}`}>
          <BookIcon book={book} style={{ height: "30px" }} />
        </div>
        <div>{linetext}</div>
        <div className="mobile-chevron-con">
          {showBetNowLink && (
            <MetaBetLink
              href={book.prismicOffers?.[0]?.web_link}
              target="_blank"
              rel="noopener"
              onClick={() => {
                AnalyticsService.track("Bet Now", {
                  page: pageLocation.includes("game-predictions") ? "Game Detail" : "Games Table",
                  url: pageLocation,
                  sport: currentSportObj?.name,
                  book: book.name,
                  state: context.userLocale,
                  component: "GameLines",
                  lineType,
                });
              }}
              bookName={book.name}
              sportsdataGameId={game?.sportsdataGameId}
            >
              Bet Now
              <Icon path={mdiChevronRight} size="30px" />
            </MetaBetLink>
          )}
        </div>
      </div>
    );
  };

  const placeholderSportbookEl = () => (
    <a className={`sportbook-con ${size}`} key={Math.random()}>
      <div className={`mobile-sportsbook-logo-name-con ${size} placeholder`} style={{ width: 30, height: 30 }} />
      <div className="placeholder" style={{ width: 68 }}>
        &nbsp;
      </div>
      <div className="mobile-chevron-con" />
    </a>
  );

  /*
   * The element that contains all sportsbook entries.
   * This is a vertical list that is truncated at 3 sportsbooks initially.  If there is a truncation then
   * we provide a see all sportsbook link at the bottom of the list.  If they click that list then all
   * books are shown.  There is no way for the user to collapse the list again and for the duration of
   * the page existence all books will be shown.
   */
  const lineScrollList = () => {
    const { books } = props;

    const ourPick = BetQLService.getOurPick(game, "value", lineType, currentSportObj ?? DEFAULT_SPORT);

    /*
     * Filter and sort the lines into a list of current lines that will be displayed
     * - Only lineType of current will be shown
     * - We match the line period to the period selected above
     * - We make sure that if new jersey is selected or another specific state only books in that state are chosen by locale
     * - If all states are selected we show all books with default locale
     *
     * The list is then sorted by rank of the sportsbooks
     */
    const items = orderBooks(books, lineType);

    /*
     * If there are more than 5 lines to show we truncate it and append a See All row for the user to expand the list.
     * The See All and truncation only happens if the showAllBooks flag has not already been tripped by the user.
     */
    let linesList = items.length
      ? items.map((item: any) => sportbookEl(ourPick, item))
      : [1, 2, 3, 4, 5].map(placeholderSportbookEl);
    if (linesList.length > 5 && !showAllBooks) {
      linesList = linesList.slice(0, 5);
      linesList.push(
        <button className="mobile-seeall-text" key="more" onClick={() => setShowAllBooks(true)}>
          See All Sportsbooks
        </button>
      );
    } else if (linesList.length > 5 && showAllBooks) {
      linesList.push(
        <button className="mobile-seeall-text" key="more" onClick={() => setShowAllBooks(false)}>
          Close
        </button>
      );
    }
    // Render the list of elements we want to show
    return <div className={`lines-list-con ${size}`}>{linesList}</div>;
  };

  return (
    <div className={`mobile-lines-con ${size}`}>
      {!loading && currentSportObj ? (
        lineScrollList()
      ) : (
        <div className={`lines-list-con ${size}`}>{[1, 2, 3, 4, 5].map(placeholderSportbookEl)}</div>
      )}
      <style jsx global>
        {styles}
      </style>
    </div>
  );
};

export default MobileGameLinesRow;
